import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Car,
  Search,
  Filter,
  X,
  CheckCircle2,
  MapPin,
  Weight,
  Calendar,
  Truck,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Types de véhicules
const VEHICLE_TYPES = [
  { value: 'plateau', label: 'Plateau', icon: '🚛' },
  { value: 'benne', label: 'Benne', icon: '🚜' },
  { value: 'citerne', label: 'Citerne', icon: '🛢️' },
  { value: 'frigo', label: 'Frigo', icon: '❄️' },
  { value: 'porte_conteneurs', label: 'Porte-conteneurs', icon: '📦' },
  { value: 'fourgon', label: 'Fourgon', icon: '🚐' },
  { value: 'remorque', label: 'Remorque', icon: '🚚' },
  { value: 'autre', label: 'Autre', icon: '🚗' }
];

// Zones géographiques
const ZONES = [
  { value: 'abidjan', label: 'Abidjan' },
  { value: 'bouake', label: 'Bouaké' },
  { value: 'yamoussoukro', label: 'Yamoussoukro' },
  { value: 'san_pedro', label: 'San-Pédro' },
  { value: 'korhogo', label: 'Korhogo' },
  { value: 'man', label: 'Man' },
  { value: 'daloa', label: 'Daloa' },
  { value: 'national', label: 'National' },
  { value: 'international', label: 'International' }
];

// Statuts de disponibilité
const AVAILABILITY_STATUS = [
  { value: 'available', label: 'Disponible', color: 'bg-green-100 text-green-800' },
  { value: 'in_mission', label: 'En mission', color: 'bg-blue-100 text-blue-800' },
  { value: 'maintenance', label: 'En maintenance', color: 'bg-amber-100 text-amber-800' },
  { value: 'unavailable', label: 'Indisponible', color: 'bg-red-100 text-red-800' }
];

/**
 * VehicleSelectorModal - Modal de sélection de véhicule avec recherche avancée
 * @param {boolean} open - État d'ouverture du modal
 * @param {function} onClose - Callback de fermeture
 * @param {function} onSelect - Callback de sélection (reçoit le véhicule sélectionné)
 * @param {string} driverId - ID du chauffeur (optionnel, pour filtrer sa flotte)
 * @param {object} filters - Filtres pré-appliqués (type, minCapacity, etc.)
 * @param {boolean} showDriverFleet - Afficher la flotte du chauffeur en priorité
 */
const VehicleSelectorModal = ({ 
  open, 
  onClose, 
  onSelect, 
  driverId = null,
  filters: initialFilters = {},
  showDriverFleet = true
}) => {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [driverVehicles, setDriverVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  // Filtres de recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: initialFilters.type || 'all',
    minCapacity: initialFilters.minCapacity || '',
    maxCapacity: initialFilters.maxCapacity || '',
    zone: initialFilters.zone || 'all',
    availability: initialFilters.availability || 'available',
    onlyWithValidDocuments: false
  });

  // Charger les véhicules
  useEffect(() => {
    if (open) {
      fetchVehicles();
      if (driverId && showDriverFleet) {
        fetchDriverVehicles(driverId);
      }
    }
  }, [open, driverId, showDriverFleet]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehicles`, {
        params: {
          available_only: filters.availability === 'available'
        }
      });
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les véhicules',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverVehicles = async (driverId) => {
    try {
      const response = await axios.get(
        `${API}/resource-assignments/driver/${driverId}/vehicles`,
        {}
      );
      setDriverVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching driver vehicles:', error);
      setDriverVehicles([]);
    }
  };

  // Filtrer les véhicules
  const filteredVehicles = useMemo(() => {
    let result = [...vehicles];

    // Recherche textuelle
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(v => 
        v.plate?.toLowerCase().includes(query) ||
        v.registration?.toLowerCase().includes(query) ||
        v.brand?.toLowerCase().includes(query) ||
        v.model?.toLowerCase().includes(query) ||
        v.type?.toLowerCase().includes(query) ||
        v.vehicleType?.toLowerCase().includes(query)
      );
    }

    // Filtre par type
    if (filters.type && filters.type !== 'all') {
      result = result.filter(v => 
        v.type?.toLowerCase() === filters.type.toLowerCase() ||
        v.vehicleType?.toLowerCase() === filters.type.toLowerCase()
      );
    }

    // Filtre par capacité min
    if (filters.minCapacity) {
      const minCap = parseFloat(filters.minCapacity);
      result = result.filter(v => (v.capacity || 0) >= minCap);
    }

    // Filtre par capacité max
    if (filters.maxCapacity) {
      const maxCap = parseFloat(filters.maxCapacity);
      result = result.filter(v => (v.capacity || 0) <= maxCap);
    }

    // Filtre par zone
    if (filters.zone && filters.zone !== 'all') {
      result = result.filter(v => 
        v.zone?.toLowerCase() === filters.zone.toLowerCase() ||
        v.zones?.includes(filters.zone)
      );
    }

    // Filtre par disponibilité
    if (filters.availability && filters.availability !== 'all') {
      result = result.filter(v => {
        if (filters.availability === 'available') {
          return v.isAvailable !== false && v.status !== 'in_mission' && v.status !== 'maintenance';
        }
        return v.status === filters.availability;
      });
    }

    // Filtre documents valides
    if (filters.onlyWithValidDocuments) {
      const now = new Date();
      result = result.filter(v => {
        const insuranceValid = !v.insuranceExpiry || new Date(v.insuranceExpiry) > now;
        const technicalValid = !v.technicalVisitExpiry || new Date(v.technicalVisitExpiry) > now;
        return insuranceValid && technicalValid;
      });
    }

    return result;
  }, [vehicles, searchQuery, filters]);

  // Filtrer les véhicules du chauffeur
  const filteredDriverVehicles = useMemo(() => {
    if (!searchQuery) return driverVehicles;
    
    const query = searchQuery.toLowerCase();
    return driverVehicles.filter(v => 
      v.plate?.toLowerCase().includes(query) ||
      v.brand?.toLowerCase().includes(query) ||
      v.model?.toLowerCase().includes(query) ||
      v.type?.toLowerCase().includes(query)
    );
  }, [driverVehicles, searchQuery]);

  const handleSelect = () => {
    if (selectedVehicle) {
      onSelect(selectedVehicle);
      onClose();
    }
  };

  const resetFilters = () => {
    setFilters({
      type: 'all',
      minCapacity: '',
      maxCapacity: '',
      zone: 'all',
      availability: 'available',
      onlyWithValidDocuments: false
    });
    setSearchQuery('');
  };

  const getVehicleTypeIcon = (type) => {
    const found = VEHICLE_TYPES.find(t => 
      t.value === type?.toLowerCase() || t.label.toLowerCase() === type?.toLowerCase()
    );
    return found?.icon || '🚗';
  };

  const isDocumentExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const isDocumentExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = (date - now) / (1000 * 60 * 60 * 24);
    return diffDays < 30 && diffDays > 0;
  };

  const VehicleCard = ({ vehicle, isDriverVehicle = false }) => {
    const isSelected = selectedVehicle?.id === vehicle.id;
    const plate = vehicle.plate || vehicle.registration;
    const type = vehicle.type || vehicle.vehicleType;
    
    return (
      <div
        onClick={() => setSelectedVehicle(vehicle)}
        className={`
          p-4 border rounded-lg cursor-pointer transition-all
          ${isSelected 
            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20' 
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }
        `}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getVehicleTypeIcon(type)}</span>
            <div>
              <p className="font-semibold text-gray-900">{plate}</p>
              <p className="text-sm text-gray-600">{type}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isDriverVehicle && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                Flotte perso
              </Badge>
            )}
            {isSelected && (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
          {vehicle.brand && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Truck className="w-3.5 h-3.5" />
              <span>{vehicle.brand} {vehicle.model}</span>
            </div>
          )}
          
          {vehicle.capacity && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Weight className="w-3.5 h-3.5" />
              <span>{vehicle.capacity}T</span>
            </div>
          )}
          
          {vehicle.zone && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <MapPin className="w-3.5 h-3.5" />
              <span>{vehicle.zone}</span>
            </div>
          )}
          
          {vehicle.year && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Calendar className="w-3.5 h-3.5" />
              <span>{vehicle.year}</span>
            </div>
          )}
        </div>

        {/* Alertes documents */}
        {(isDocumentExpired(vehicle.insuranceExpiry) || isDocumentExpired(vehicle.technicalVisitExpiry)) && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Documents expirés</span>
          </div>
        )}
        {(isDocumentExpiringSoon(vehicle.insuranceExpiry) || isDocumentExpiringSoon(vehicle.technicalVisitExpiry)) && 
         !isDocumentExpired(vehicle.insuranceExpiry) && !isDocumentExpired(vehicle.technicalVisitExpiry) && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Documents bientôt expirés</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-emerald-600" />
            Sélectionner un véhicule
          </DialogTitle>
          <DialogDescription>
            Recherchez et sélectionnez un véhicule pour cette opération
          </DialogDescription>
        </DialogHeader>

        {/* Barre de recherche */}
        <div className="flex items-center gap-3 py-3 border-b">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par immatriculation, marque, modèle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-emerald-600' : ''}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filtres
          </Button>
        </div>

        {/* Panneau de filtres */}
        {showFilters && (
          <div className="py-3 px-1 border-b bg-gray-50 rounded-lg my-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Type de véhicule */}
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select 
                  value={filters.type} 
                  onValueChange={(value) => setFilters({...filters, type: value})}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {VEHICLE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Capacité min */}
              <div className="space-y-1">
                <Label className="text-xs">Capacité min (T)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minCapacity}
                  onChange={(e) => setFilters({...filters, minCapacity: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>

              {/* Capacité max */}
              <div className="space-y-1">
                <Label className="text-xs">Capacité max (T)</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={filters.maxCapacity}
                  onChange={(e) => setFilters({...filters, maxCapacity: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>

              {/* Zone */}
              <div className="space-y-1">
                <Label className="text-xs">Zone</Label>
                <Select 
                  value={filters.zone} 
                  onValueChange={(value) => setFilters({...filters, zone: value})}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les zones</SelectItem>
                    {ZONES.map(zone => (
                      <SelectItem key={zone.value} value={zone.value}>
                        {zone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="validDocs"
                  checked={filters.onlyWithValidDocuments}
                  onCheckedChange={(checked) => 
                    setFilters({...filters, onlyWithValidDocuments: checked})
                  }
                />
                <Label htmlFor="validDocs" className="text-xs cursor-pointer">
                  Documents à jour uniquement
                </Label>
              </div>
              
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Réinitialiser
              </Button>
            </div>
          </div>
        )}

        {/* Liste des véhicules */}
        <div className="flex-1 overflow-y-auto py-2 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Flotte du chauffeur */}
              {showDriverFleet && filteredDriverVehicles.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">
                      {filteredDriverVehicles.length}
                    </Badge>
                    Flotte du chauffeur
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredDriverVehicles.map(vehicle => (
                      <VehicleCard 
                        key={vehicle.id} 
                        vehicle={vehicle} 
                        isDriverVehicle={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Flotte globale */}
              {filteredVehicles.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Badge variant="outline">
                      {filteredVehicles.length}
                    </Badge>
                    Flotte globale
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredVehicles.map(vehicle => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} />
                    ))}
                  </div>
                </div>
              )}

              {/* Aucun résultat */}
              {filteredVehicles.length === 0 && filteredDriverVehicles.length === 0 && (
                <div className="text-center py-12">
                  <Car className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Aucun véhicule trouvé</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Essayez de modifier vos critères de recherche
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSelect} 
            disabled={!selectedVehicle}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Sélectionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleSelectorModal;
